import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  FloatButton,
  List,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { CheckOutlined, CloseOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  useMyEnrollments,
  useRegistrationWindow,
  useSuggestedCourses,
  type SuggestedCourse,
} from "@/api/enrollment";
import {
  formatAmountVND,
  useCartCheckout,
  type CartCheckoutSkippedItem,
  type Order,
  type OrderItem,
} from "@/api/payment";
import { useCourseCart, type CourseCart } from "@/hooks/useCourseCart";

const { Text } = Typography;

const REASON_TEXT: Record<string, string> = {
  no_active_major: "Bạn chưa theo ngành nào.",
  no_terms_defined: "Ngành này chưa có khung chương trình.",
  completed_all_terms: "Bạn đã hoàn thành lộ trình gợi ý — không còn môn nào để gợi ý thêm.",
};

const SKIP_REASON_TEXT: Record<string, string> = {
  not_found: "Không tìm thấy môn học",
  tenant_mismatch: "Bạn không có quyền với môn học này",
  blocked_prerequisites: "Chưa đủ điều kiện tiên quyết",
  pricing_not_configured: "Môn học chưa thiết lập học phí",
  already_enrolled: "Bạn đã đăng ký môn học này rồi",
};

/** Backend (`suggested_courses_for_term`) đã loại ENROLLED/COMPLETED khỏi
 * danh sách này — trạng thái enrollment thực tế còn lại chỉ có thể là
 * DROPPED (giữ lại để đăng ký lại) hoặc chưa từng đăng ký. Sếp phản hồi
 * 2026-07-13: phải ghi rõ trạng thái từng môn, không để sinh viên đoán. */
function statusTag(status: "dropped" | undefined): { label: string; color: string } {
  if (status === "dropped") return { label: "Đã bỏ — đăng ký lại", color: "default" };
  return { label: "Chưa đăng ký", color: "default" };
}

/** null (PER_CREDIT chưa cấu hình) → "Chưa có giá"; 0 → "Miễn phí"; else format VND. */
function formatPrice(cents: number | null): string {
  if (cents === null) return "Chưa có giá";
  if (cents === 0) return "Miễn phí";
  return formatAmountVND(cents);
}

function skippedItemsMessage(skipped: CartCheckoutSkippedItem[]): string {
  const names = skipped.map((s) => `${s.course_slug} (${SKIP_REASON_TEXT[s.status] ?? s.detail})`);
  return `${skipped.length} môn bị bỏ qua: ${names.join(", ")}`;
}

/** Tra cứu tên/giá/loại của 1 môn KHÔNG nằm trong danh sách gợi ý kỳ hiện
 * tại — dùng để Modal giỏ hàng hiện đúng thông tin cho môn "học vượt" thêm
 * từ `MajorTermsCard` (Sprint 35). */
export interface CatalogEntry {
  code: string;
  name: string;
  slug: string;
  is_required: boolean;
  effective_price_cents: number | null;
}

/** 1 dòng trong bảng giỏ hàng — subset field thực sự render, để nhận được cả
 * `SuggestedCourse` (kỳ hiện tại) lẫn `CatalogEntry` (học vượt, kỳ khác). */
interface CartLineCourse {
  code: string;
  name: string;
  slug: string;
  is_required: boolean;
  effective_price_cents: number | null;
}

interface SuggestedCoursesProps {
  /** Ngành cần gợi ý. Bỏ trống ở variant "banner" khi chưa xác định được
   * đúng 1 ngành đang học (parent tự quyết định có render component không). */
  majorId?: number;
  /** "banner" — rút gọn, tối đa 4 môn, ẩn hẳn khi không có gì gợi ý.
   *  "full" — hiện đầy đủ + trạng thái rỗng có giải thích, dùng ở trang chi
   *  tiết ngành. Cả 2 variant đều dùng giỏ hàng chọn nhiều môn đăng ký 1 lần,
   *  gộp thành 1 đơn hàng duy nhất kể cả trộn môn free + có phí (Sprint 34). */
  variant: "banner" | "full";
  /** Giỏ hàng dùng CHUNG — `StudyPlan.tsx` truyền vào (Sprint 35) để gộp
   * chung với môn "học vượt" chọn từ `MajorTermsCard`. Không truyền (banner
   * ở `CourseList.tsx`, `MajorPublicDetail.tsx`) → component tự quản lý giỏ
   * hàng riêng bằng `useCourseCart` nội bộ, y hệt hành vi trước Sprint 35. */
  cart?: CourseCart;
  /** Tra cứu môn học vượt (không nằm trong `data.courses` — kỳ hiện tại) để
   * Modal giỏ hàng hiện đúng tên/giá thay vì bị lọc mất. Chỉ có ý nghĩa khi
   * `cart` cũng được truyền (nơi khác không ai ghi slug lạ vào giỏ). */
  catalogLookup?: Map<string, CatalogEntry>;
}

export function SuggestedCourses({
  majorId,
  variant,
  cart: externalCart,
  catalogLookup,
}: SuggestedCoursesProps) {
  const { data, isLoading } = useSuggestedCourses(majorId);
  const { data: enrollments } = useMyEnrollments();
  // Sếp phản hồi 2026-07-13: nút "Thêm vào giỏ hàng" trước đây không biết gì
  // về đợt đăng ký (chỉ checkout mới check `resolve_open_term`, sinh viên
  // chọn xong mới phát hiện bị chặn). `useRegistrationWindow` không có tham
  // số → React Query tự dedupe dù nhiều component (StudyPlan, MajorTermsCard)
  // cùng gọi, không cần truyền prop qua nhiều tầng.
  const { data: registrationWindow } = useRegistrationWindow();
  const registrationOpen = registrationWindow?.is_open ?? true;
  const droppedSlugs = useMemo(
    () => new Set((enrollments ?? []).filter((e) => e.status === "dropped").map((e) => e.course.slug)),
    [enrollments],
  );
  const cartCheckout = useCartCheckout();
  // Luôn gọi hook (rules-of-hooks) — chỉ THỰC SỰ dùng khi không có `cart`
  // truyền từ ngoài (`ownCart` rẻ, chỉ đọc/ghi localStorage).
  const ownCart = useCourseCart(majorId);
  const cart = externalCart ?? ownCart;
  const { selected } = cart;
  const [cartOpen, setCartOpen] = useState(false);
  const [paidOrder, setPaidOrder] = useState<Order | null>(null);

  const hasCourses = !!data && !data.reason && data.courses.length > 0;
  const displayedCourses: SuggestedCourse[] = useMemo(
    () => (variant === "banner" ? (data?.courses.slice(0, 4) ?? []) : (data?.courses ?? [])),
    [data, variant],
  );
  // Giỏ hàng chỉ thêm được môn chưa bị chặn tiên quyết VÀ đang trong đợt
  // đăng ký — môn blocked/ngoài đợt vẫn hiện trong list để sinh viên biết
  // nằm trong lộ trình, nhưng không thể thêm giỏ (nút "Thêm tất cả" ẩn hẳn
  // khi ngoài đợt — banner phía trên đã giải thích lý do).
  const eligibleSlugs = useMemo(
    () => (registrationOpen ? displayedCourses.filter((c) => !c.blocked).map((c) => c.slug) : []),
    [displayedCourses, registrationOpen],
  );
  // Map theo `selected` (không filter từ `displayedCourses`) — giỏ hàng có
  // thể chứa slug "học vượt" không thuộc kỳ hiện tại (thêm từ
  // `MajorTermsCard`), tra cứu thêm qua `catalogLookup` để hiện đúng tên/giá.
  const cartCourses: CartLineCourse[] = useMemo(
    () =>
      selected.map((slug) => {
        const fromCurrentTerm = displayedCourses.find((c) => c.slug === slug);
        if (fromCurrentTerm) return fromCurrentTerm;
        const fromCatalog = catalogLookup?.get(slug);
        if (fromCatalog) return fromCatalog;
        // Hiếm — dữ liệu catalog chưa kịp load. Checkout vẫn gửi đúng slug.
        return { code: slug, name: slug, slug, is_required: false, effective_price_cents: null };
      }),
    [displayedCourses, selected, catalogLookup],
  );
  const cartTotalCents = useMemo(
    () => cartCourses.reduce((sum, c) => sum + (c.effective_price_cents ?? 0), 0),
    [cartCourses],
  );

  const toggleCartItem = cart.toggle;
  const addAllEligible = () => cart.addAll(eligibleSlugs);
  const removeFromCart = cart.remove;

  const handleCartCheckout = async () => {
    try {
      const order = await cartCheckout.mutateAsync(selected);
      if (order.skipped_items.length > 0) {
        message.warning(skippedItemsMessage(order.skipped_items), 8);
      }
      cart.clear();
      if (order.status === "paid") {
        message.success(`Đăng ký thành công ${order.items.length} môn!`);
        setPaidOrder(order);
      } else {
        setCartOpen(false);
        window.location.href = order.payment_link;
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { detail?: string } } })?.response?.data;
      message.error(data?.detail ?? "Đăng ký thất bại. Vui lòng thử lại.", 6);
    }
  };

  const closeCart = () => {
    setCartOpen(false);
    setPaidOrder(null);
  };

  if (isLoading) {
    return variant === "full" ? (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    ) : null;
  }

  if (!hasCourses) {
    if (variant === "banner") return null;
    // Giỏ hàng chung (Sprint 35, `StudyPlan` truyền `externalCart`) phải vẫn
    // mở được để hoàn tất checkout môn "học vượt" chọn từ `MajorTermsCard` —
    // kể cả khi kỳ hiện tại không có gợi ý nào (`completed_all_terms` —
    // chính là trường hợp sinh viên cuối lộ trình, hay muốn học vượt nhất).
    // Tự quản lý giỏ hàng riêng (banner/MajorPublicDetail, không có
    // `externalCart`) → giữ nguyên hành vi cũ, return Empty luôn.
    if (!externalCart) {
      return (
        <Empty
          description={data?.reason ? REASON_TEXT[data.reason] : "Chưa có môn nào để gợi ý."}
        />
      );
    }
  }

  const allEligibleSelected =
    eligibleSlugs.length > 0 && eligibleSlugs.every((s) => selected.includes(s));

  const cartColumns: ColumnsType<CartLineCourse> = [
    { title: "Mã môn", dataIndex: "code", key: "code", width: 100 },
    {
      title: "Tên môn",
      key: "name",
      render: (_, course) => <Link to={`/courses/${course.slug}`}>{course.name}</Link>,
    },
    {
      title: "Loại",
      key: "type",
      width: 90,
      render: (_, course) => (
        <Tag color={course.is_required ? "blue" : "default"}>
          {course.is_required ? "Bắt buộc" : "Tự chọn"}
        </Tag>
      ),
    },
    {
      title: "Trạng thái",
      key: "status",
      width: 140,
      render: (_, course) => {
        const tag = statusTag(droppedSlugs.has(course.slug) ? "dropped" : undefined);
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Giá",
      key: "price",
      width: 110,
      align: "right",
      render: (_, course) => formatPrice(course.effective_price_cents),
    },
    {
      title: "",
      key: "action",
      width: 48,
      render: (_, course) => (
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label={`Bỏ ${course.code} khỏi giỏ hàng`}
          onClick={() => removeFromCart(course.slug)}
        />
      ),
    },
  ];

  const paidColumns: ColumnsType<OrderItem> = [
    { title: "Mã môn", key: "code", render: (_, item) => item.course.code },
    { title: "Tên môn", key: "name", render: (_, item) => item.course.name },
    { title: "Giá", key: "price", align: "right", render: (_, item) => item.amount_display },
  ];

  return (
    <>
      {variant === "full" && !registrationOpen && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Hiện không trong đợt đăng ký môn học nào"
          description="Bạn vẫn có thể xem trước danh sách môn, nhưng chưa thêm được vào giỏ hàng cho tới khi đợt đăng ký tiếp theo mở."
        />
      )}
      {!hasCourses && (
        <Empty
          style={{ marginBottom: 16 }}
          description={data?.reason ? REASON_TEXT[data.reason] : "Chưa có môn nào để gợi ý."}
        />
      )}
      {hasCourses && data && (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Gợi ý cho kỳ hiện tại — ${data.current_term?.name}`}
        description={
          <>
            {eligibleSlugs.length > 0 && (
              <Button
                size="small"
                style={{ marginBottom: 8 }}
                disabled={allEligibleSelected}
                onClick={addAllEligible}
              >
                {allEligibleSelected ? "Đã thêm tất cả" : "Thêm tất cả vào giỏ hàng"}
              </Button>
            )}
            <List
              size="small"
              dataSource={displayedCourses}
              renderItem={(course) => {
                const inCart = selected.includes(course.slug);
                const tag = statusTag(droppedSlugs.has(course.slug) ? "dropped" : undefined);
                return (
                  <List.Item
                    key={course.id}
                    actions={[
                      <Tooltip
                        key="add-to-cart"
                        title={
                          course.blocked
                            ? `Chưa đủ điều kiện, còn thiếu: ${course.missing_prerequisites
                                .map((p) => `${p.code} - ${p.name}`)
                                .join(", ")}`
                            : !registrationOpen && !inCart
                              ? "Hiện không trong đợt đăng ký môn học nào."
                              : undefined
                        }
                      >
                        <Button
                          size="small"
                          type={inCart ? "default" : "primary"}
                          icon={inCart ? <CheckOutlined /> : <ShoppingCartOutlined />}
                          disabled={course.blocked || (!registrationOpen && !inCart)}
                          aria-label={
                            inCart ? `Bỏ chọn ${course.code}` : `Thêm ${course.code} vào giỏ hàng`
                          }
                          onClick={() => toggleCartItem(course.slug)}
                        >
                          {inCart ? "Đã thêm" : "Thêm vào giỏ hàng"}
                        </Button>
                      </Tooltip>,
                    ]}
                  >
                    <Space>
                      <Link to={`/courses/${course.slug}`}>
                        {course.code} — {course.name}
                      </Link>
                      <Tag color={course.is_required ? "blue" : "default"}>
                        {course.is_required ? "Bắt buộc" : "Tự chọn"}
                      </Tag>
                      <Text type="secondary">{formatPrice(course.effective_price_cents)}</Text>
                      <Tag color={tag.color}>{tag.label}</Tag>
                      {course.blocked && <Tag color="warning">Chưa đủ điều kiện</Tag>}
                    </Space>
                  </List.Item>
                );
              }}
            />
          </>
        }
      />
      )}

      <FloatButton
        icon={<ShoppingCartOutlined />}
        type="primary"
        tooltip="Xem giỏ hàng đăng ký"
        badge={{ count: selected.length, color: "red" }}
        onClick={() => setCartOpen(true)}
        style={{ right: 24, bottom: 24 }}
        data-testid="cart-fab"
      />

      <Modal
        open={cartOpen}
        onCancel={closeCart}
        width={720}
        title={
          paidOrder
            ? "Kết quả đăng ký"
            : `Giỏ hàng đăng ký môn học (${cartCourses.length} môn) — Tổng: ${formatPrice(cartTotalCents)}`
        }
        footer={
          paidOrder ? (
            <Button type="primary" onClick={closeCart}>
              Đóng
            </Button>
          ) : (
            <Space>
              <Button onClick={closeCart}>Huỷ</Button>
              <Button
                type="primary"
                disabled={selected.length === 0}
                loading={cartCheckout.isPending}
                onClick={handleCartCheckout}
              >
                Đăng ký {selected.length} môn
              </Button>
            </Space>
          )
        }
      >
        {paidOrder ? (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={paidOrder.items}
            columns={paidColumns}
          />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="slug"
            dataSource={cartCourses}
            columns={cartColumns}
            locale={{ emptyText: "Chưa chọn môn nào." }}
          />
        )}
      </Modal>
    </>
  );
}

export default SuggestedCourses;
